import { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { message } from '../utils/antdMessage';
import apiFetch from '../utils/apiFetch';
import siteConfig from '../config/site-config.js';
import { toMapLngLat, fromMapLngLat } from '../utils/dashboard/basemapCoord';
import { resolveRasterTilesForMap } from '../utils/dashboard/gcjRasterTiles';
import {
  attachOrthoGlbOnMap,
  isOrthoGlbOnMap,
  removeAllOrthoGlbOverlays,
  resolveModelPose,
  setOrthoGlbGizmoActive,
  setOrthoGlbMarkerDraggable,
  toggleOrthoGlbOnMap,
  updateOrthoGlbPose
} from '../utils/orthoGlbMapLayer';
import {
  parseMapLayerBounds,
  bboxIntersects,
  isOrthoRasterOnMap
} from '../utils/orthoToolbox';
import orthoReadJson from './orthoReadJson';

/**
 * 2D/3D 种类开关、GLB 查看与地图叠加
 */
export default function useOrthoKindOverlays({
  getMap,
  onStartInteract,
  expanded,
  setExpanded,
  setPinned,
  pinForSecondaryMenu,
  setBusy,
  layers,
  loadLayers,
  isMobile = false
}) {
  const [glbViewer, setGlbViewer] = useState(null); // { layerId, name, url }
  const [glbMapActive, setGlbMapActive] = useState({}); // layerId -> bool
  const [glbLoading, setGlbLoading] = useState({}); // layerId -> 正在拉细模
  const glbLoadingRef = useRef({});
  const glbAbortRef = useRef({});
  /** 2D航拍 / 3D建模：与路网同款（文字开菜单；复选框才叠加） */
  const [orthoKindChecked, setOrthoKindChecked] = useState({ '2d': false, '3d': false });
  const [orthoKindMenuOpen, setOrthoKindMenuOpen] = useState({ '2d': false, '3d': false });
  /** 2D 正射是否已叠加（与地图 layer 同步） */
  const [ortho2dActive, setOrtho2dActive] = useState({});
  /** 3D 位姿微调弹窗 */
  const [poseEditor, setPoseEditor] = useState(null); // layer object
  const [poseSaving, setPoseSaving] = useState(false);
  const poseEditorRef = useRef(null);
  poseEditorRef.current = poseEditor;

  const emitPoseDrag = (map, layerId, ll) => {
    const wgs = fromMapLngLat(ll.lng, ll.lat, undefined, map) || [ll.lng, ll.lat];
    const detail = { layerId, lng: wgs[0], lat: wgs[1] };
    if (Number.isFinite(Number(ll.altitude))) detail.altitude = Number(ll.altitude);
    window.dispatchEvent(new CustomEvent('zhxt-ortho-pose-drag', { detail }));
  };

  const bindPoseInteract = (map, layer) => {
    if (!map || !layer?.id) return;
    const onMove = (ll) => emitPoseDrag(map, layer.id, ll);
    const gizmoOk = setOrthoGlbGizmoActive(map, layer.id, true, onMove);
    if (!gizmoOk) {
      setOrthoGlbMarkerDraggable(map, layer.id, true, onMove);
    }
  };

  const unbindPoseInteract = (map, layerId) => {
    if (!map || layerId == null) return;
    setOrthoGlbGizmoActive(map, layerId, false);
    setOrthoGlbMarkerDraggable(map, layerId, false);
  };

  const openGlbViewer = async (layer) => {
    if (!layer?.id) return;
    try {
      const token = localStorage.getItem('token') || '';
      const apiBase = String(siteConfig.getApiUrl() || '/api').replace(/\/$/, '');
      const url = `${apiBase}/ortho/layers/${layer.id}/glb?access_token=${encodeURIComponent(token)}`;
      setGlbViewer({
        layerId: layer.id,
        name: layer.name || `图层 #${layer.id}`,
        url
      });
    } catch (e) {
      message.error(e.message || '无法打开 3D 查看');
    }
  };

  const attachGlbToLayer = async (layer) => {
    if (!layer?.id) return;
    setBusy(true);
    try {
      const data = await orthoReadJson(await apiFetch(`/api/ortho/layers/${layer.id}/attach-glb`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      }));
      message.success(data.message || `已登记 3D：${data.glbOssKey || ''}`);
      await loadLayers();
    } catch (e) {
      message.error(e.message || '登记 3D 失败');
    } finally {
      setBusy(false);
    }
  };

  const openPoseEditor = (layer) => {
    if (!layer?.id) return;
    if (!layer.glbOssKey) {
      message.warning('请先登记 3D 模型');
      return;
    }
    const map = getMap?.();
    if (!isOrthoGlbOnMap(map, layer.id)) {
      message.info('请先勾选叠加该 3D，再微调（模型上会出现三轴箭头）');
    }
    setPoseEditor(layer);
    bindPoseInteract(map, layer);
  };

  /** draft 为 WGS；贴地图显示需转当前底图坐标 */
  const previewModelPose = (draft) => {
    if (!poseEditor?.id || !draft) return;
    const map = getMap?.();
    if (!map) return;
    const mapped = toMapLngLat(draft.lng, draft.lat, undefined, map);
    updateOrthoGlbPose(map, poseEditor.id, {
      ...draft,
      lng: mapped?.[0] ?? draft.lng,
      lat: mapped?.[1] ?? draft.lat,
      followTerrain: Number(draft.altitude) === 0
    });
  };

  const saveModelPose = async (draft) => {
    if (!poseEditor?.id || !draft) return;
    setPoseSaving(true);
    try {
      const data = await orthoReadJson(await apiFetch(`/api/ortho/layers/${poseEditor.id}/model-pose`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft)
      }));
      message.success('位姿已保存');
      previewModelPose(data.modelPose || draft);
      unbindPoseInteract(getMap?.(), poseEditor.id);
      setPoseEditor(null);
      await loadLayers?.();
    } catch (e) {
      message.error(e.message || '保存位姿失败');
    } finally {
      setPoseSaving(false);
    }
  };

  const closePoseEditor = () => {
    const layer = poseEditor;
    const map = getMap?.();
    if (layer?.id && map) {
      unbindPoseInteract(map, layer.id);
      const saved = resolveModelPose(layer);
      if (saved) {
        const mapped = toMapLngLat(saved.lng, saved.lat, undefined, map);
        updateOrthoGlbPose(map, layer.id, {
          ...saved,
          lng: mapped?.[0] ?? saved.lng,
          lat: mapped?.[1] ?? saved.lat
        });
      }
    }
    setPoseEditor(null);
  };

  const markGlbLoading = (layerId, on) => {
    glbLoadingRef.current = { ...glbLoadingRef.current, [layerId]: on };
    setGlbLoading((prev) => ({ ...prev, [layerId]: on }));
  };

  const toggleGlbOnMap = async (layer) => {
    const map = getMap?.();
    if (!map || !layer?.id) return;
    if (!layer.glbOssKey) {
      message.warning('请先「登记 3D 模型」');
      return;
    }
    const wasOn = isOrthoGlbOnMap(map, layer.id);
    if (wasOn) {
      try {
        glbAbortRef.current[layer.id]?.abort?.();
      } catch (_) { /* ignore */ }
      delete glbAbortRef.current[layer.id];
      await toggleOrthoGlbOnMap(map, layer, {});
      markGlbLoading(layer.id, false);
      setGlbMapActive((prev) => ({ ...prev, [layer.id]: false }));
      message.success('已关闭地图 3D');
      return { active: false };
    }
    try {
      onStartInteract?.();
      ensureOrtho2dOff(layer);
      const token = localStorage.getItem('token') || '';
      const apiBase = String(siteConfig.getApiUrl() || '/api').replace(/\/$/, '');
      const glbUrl = `${apiBase}/ortho/layers/${layer.id}/glb?access_token=${encodeURIComponent(token)}`;
      const pose = resolveModelPose(layer);
      let displayLng;
      let displayLat;
      if (pose && Number.isFinite(pose.lng) && Number.isFinite(pose.lat)) {
        const mapped = toMapLngLat(pose.lng, pose.lat, undefined, map);
        displayLng = mapped?.[0];
        displayLat = mapped?.[1];
      }
      const ac = new AbortController();
      glbAbortRef.current[layer.id] = ac;
      setGlbMapActive((prev) => ({ ...prev, [layer.id]: true }));
      markGlbLoading(layer.id, true);
      const result = await attachOrthoGlbOnMap(map, layer, {
        glbUrl,
        displayLng,
        displayLat,
        signal: ac.signal,
        onReady: () => {
          markGlbLoading(layer.id, false);
          message.success('3D 细节已加载，可右键拖转绕飞');
        },
        onError: (err) => {
          markGlbLoading(layer.id, false);
          setGlbMapActive((prev) => ({ ...prev, [layer.id]: false }));
          message.error(err?.message || '地图附着 3D 失败');
        }
      });
      message.info('已勾选：先显示粗轮廓，细节后台加载');
      if (poseEditorRef.current?.id === layer.id) {
        bindPoseInteract(map, poseEditorRef.current);
      }
      result.pending?.catch?.(() => {});
      return result;
    } catch (e) {
      console.error(e);
      markGlbLoading(layer.id, false);
      setGlbMapActive((prev) => ({ ...prev, [layer.id]: false }));
      message.error(e.message || '地图附着 3D 失败');
      return { active: false };
    }
  };

  useEffect(() => {
    const map = getMap?.();
    if (!map || !expanded) return undefined;
    const sync = () => {
      const nextGlb = {};
      const next2d = {};
      (layers || []).forEach((ly) => {
        nextGlb[ly.id] = isOrthoGlbOnMap(map, ly.id) || !!glbLoadingRef.current[ly.id];
        next2d[ly.id] = isOrthoRasterOnMap(map, ly.id);
      });
      setGlbMapActive(nextGlb);
      setOrtho2dActive(next2d);
    };
    sync();
    const t = setInterval(sync, 3000);
    return () => clearInterval(t);
  }, [getMap, layers, expanded]);

  const toggleLayerOnMap = async (layer) => {
    const map = getMap?.();
    if (!map || !layer) return;
    const sourceId = `zhxt-ortho-layer-${layer.id}`;
    const layerId = `${sourceId}-raster`;
    try {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        setOrtho2dActive((prev) => ({ ...prev, [layer.id]: false }));
        message.info('已关闭图层');
        return;
      }
      onStartInteract?.();
      try {
        map.stop?.();
        if (typeof map.setProjection === 'function') {
          const proj = map.getProjection?.();
          const name = typeof proj === 'string' ? proj : proj?.name;
          if (name === 'globe') map.setProjection('mercator');
        }
        map.setPitch?.(0);
        map.setBearing?.(0);
      } catch (_) { /* ignore */ }

      const token = localStorage.getItem('token') || '';
      const apiBase = String(siteConfig.getApiUrl() || '/api').replace(/\/$/, '');
      const tileUrl = `${apiBase}/ortho/layers/${layer.id}/tile/{z}/{x}/{y}.png?access_token=${encodeURIComponent(token)}`;
      // 本次切片为 14–18 级；缩太远会看不到瓦片
      const minz = Number(layer.minzoom) || 14;
      const maxz = Number(layer.maxzoom) || 18;
      if (map.getSource(sourceId)) {
        try {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
          map.removeSource(sourceId);
        } catch (_) { /* ignore */ }
      }
      // 瓦片在 OSS 上可能是 TMS；代理已按 tileScheme 翻转，前端固定按 XYZ 请求即可
      // 正射切片是 WGS84：高德/谷歌（GCJ）底图下改走前端逐像素重采样地址，与底图对齐；WGS 底图原样
      const rasterTiles = resolveRasterTilesForMap(map, tileUrl);
      map.addSource(sourceId, {
        type: 'raster',
        tiles: rasterTiles.tiles,
        tileSize: layer.tileSize || 256,
        minzoom: minz,
        maxzoom: maxz,
        scheme: 'xyz'
      });
      // 尽量压在标注之下、底图之上，避免被其它图层挡住
      let beforeId;
      try {
        const style = map.getStyle?.();
        beforeId = (style?.layers || []).find((l) =>
          /label|place|settlement|poi|road-label/i.test(l.id)
        )?.id;
      } catch (_) { /* ignore */ }
      map.addLayer({
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: {
          // 不透明，避免和底图混色发糊
          'raster-opacity': 1,
          // 放大超过瓦片级时尽量少插值发糊（部分环境仍可能偏软）
          'raster-resampling': 'nearest',
          'raster-fade-duration': 0
        }
      }, beforeId);
      setOrtho2dActive((prev) => ({ ...prev, [layer.id]: true }));
      const tipZ = maxz >= 20 ? `请放大到约 ${Math.min(maxz, 20)}～${maxz} 级看细节` : `请到约 ${minz}～${maxz} 级查看`;
      message.success(`已叠加：${layer.name || `#${layer.id}`}（${tipZ}）`);
      // 勾选只叠加，不飞行；点名称才 flyToOrthoLayer
    } catch (e) {
      message.error(e.message || '叠加失败');
    }
  };

  /** 仅飞行到图层范围（不切换叠加） */
  const flyToOrthoLayer = (layer, { silent = false } = {}) => {
    const map = getMap?.();
    if (!map || !layer) return;
    const bb = parseMapLayerBounds(layer.bounds);
    if (!bb || (Math.abs(bb.west) < 0.05 && Math.abs(bb.south) < 0.05)) {
      if (!silent) message.warning('图层范围无效，无法定位');
      return;
    }
    try {
      onStartInteract?.();
      const sw = toMapLngLat(bb.west, bb.south, undefined, map) || [bb.west, bb.south];
      const ne = toMapLngLat(bb.east, bb.north, undefined, map) || [bb.east, bb.north];
      const maxz = Number(layer.maxzoom) || 18;
      map.fitBounds([sw, ne], {
        padding: 80,
        duration: 1000,
        maxZoom: Math.min(Math.max(maxz - 1, 17), 21),
        essential: true
      });
    } catch (e) {
      if (!silent) message.warning(e.message || '定位失败');
    }
  };

  /** 仅飞行到 3D 锚点/范围（不贴模型） */
  const flyToOrtho3d = (layer) => {
    const map = getMap?.();
    if (!map || !layer) return;
    try {
      onStartInteract?.();
      const pose = resolveModelPose(layer);
      if (pose && Number.isFinite(pose.lng) && Number.isFinite(pose.lat)) {
        const mapped = toMapLngLat(pose.lng, pose.lat, undefined, map) || [pose.lng, pose.lat];
        map.easeTo({
          center: mapped,
          zoom: Math.max(map.getZoom?.() || 0, 17),
          pitch: Math.max(map.getPitch?.() || 0, 55),
          duration: 1000,
          essential: true
        });
        return;
      }
      flyToOrthoLayer(layer);
    } catch (e) {
      message.warning(e.message || '定位失败');
    }
  };

  const ensureOrtho2dOn = async (layer) => {
    const map = getMap?.();
    if (!map || !layer) return false;
    if (isOrthoRasterOnMap(map, layer.id)) {
      setOrtho2dActive((prev) => ({ ...prev, [layer.id]: true }));
      return true;
    }
    await toggleLayerOnMap(layer);
    return isOrthoRasterOnMap(getMap?.(), layer.id);
  };

  const ensureOrtho2dOff = (layer) => {
    const map = getMap?.();
    if (!map || !layer) return;
    if (isOrthoRasterOnMap(map, layer.id)) {
      try {
        const sourceId = `zhxt-ortho-layer-${layer.id}`;
        const layerId = `${sourceId}-raster`;
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch (_) { /* ignore */ }
    }
    setOrtho2dActive((prev) => ({ ...prev, [layer.id]: false }));
  };

  const ensureOrtho3dOn = async (layer) => {
    if (!layer?.glbOssKey) {
      message.warning('该图层尚未登记 3D 模型');
      return false;
    }
    const map = getMap?.();
    if (!map) return false;
    // 与 toggleGlbOnMap 一致：3D 与同层 2D 互斥，防止放大时正射盖住模型
    ensureOrtho2dOff(layer);
    if (isOrthoGlbOnMap(map, layer.id)) {
      setGlbMapActive((prev) => ({ ...prev, [layer.id]: true }));
      return true;
    }
    await toggleGlbOnMap(layer);
    return isOrthoGlbOnMap(getMap?.(), layer.id);
  };

  const ensureOrtho3dOff = async (layer) => {
    const map = getMap?.();
    if (!map || !layer) return;
    if (isOrthoGlbOnMap(map, layer.id)) {
      await toggleGlbOnMap(layer);
    }
    setGlbMapActive((prev) => ({ ...prev, [layer.id]: false }));
  };

  const turnOffOrthoKindOverlays = async (kind) => {
    const list = layers || [];
    if (kind === '2d') {
      list.forEach((ly) => ensureOrtho2dOff(ly));
      return;
    }
    Object.values(glbAbortRef.current).forEach((ac) => {
      try { ac.abort?.(); } catch (_) { /* ignore */ }
    });
    glbAbortRef.current = {};
    glbLoadingRef.current = {};
    setGlbLoading({});
    const map = getMap?.();
    if (map) {
      try {
        removeAllOrthoGlbOverlays(map);
      } catch (_) { /* ignore */ }
    }
    setGlbMapActive({});
  };

  const openOrthoKindMenu = (kind) => {
    loadLayers();
    if (typeof pinForSecondaryMenu === 'function') {
      pinForSecondaryMenu();
    } else {
      setPinned(true);
      setExpanded(true);
    }
    setOrthoKindMenuOpen((prev) => ({ ...prev, [kind]: true }));
  };

  const onOrthoKindLabelOpenChange = (kind, open) => {
    if (open) {
      openOrthoKindMenu(kind);
      return;
    }
    setOrthoKindMenuOpen((prev) => ({ ...prev, [kind]: false }));
  };

  const onOrthoKindMasterCheck = (kind, checked) => {
    if (checked) {
      setOrthoKindChecked((prev) => ({ ...prev, [kind]: true }));
      openOrthoKindMenu(kind);
      // 只开菜单，不自动加载全部图层（3D GLB 很大，全量加载会极慢；由子项复选框单独叠加）
      return;
    }
    const kindLabel = kind === '3d' ? '3D 建模' : '2D 航拍';
    const map = getMap?.();
    const activeIds = new Set();
    (layers || []).forEach((ly) => {
      if (kind === '3d') {
        if (glbMapActive[ly.id] || isOrthoGlbOnMap(map, ly.id)) activeIds.add(ly.id);
      } else if (ortho2dActive[ly.id] || isOrthoRasterOnMap(map, ly.id)) {
        activeIds.add(ly.id);
      }
    });
    const activeCount = activeIds.size;
    const applyUncheck = async () => {
      await turnOffOrthoKindOverlays(kind);
      setOrthoKindChecked((prev) => ({ ...prev, [kind]: false }));
      setOrthoKindMenuOpen((prev) => ({ ...prev, [kind]: false }));
    };
    // 仅叠加 ≥2 项时二次确认；0/1 项直接取消
    if (activeCount < 2) {
      applyUncheck();
      return;
    }
    Modal.confirm({
      title: `取消加载全部${kindLabel}？`,
      content: `是否取消加载所有${kindLabel}？当前地图上已叠加 ${activeCount} 条，确认后将全部关闭。`,
      okText: '取消加载',
      cancelText: '返回',
      okButtonProps: { danger: true },
      onOk: applyUncheck
    });
  };

  const onOrthoItemCheck = async (kind, layer, checked) => {
    if (checked) {
      const ok = kind === '3d' ? await ensureOrtho3dOn(layer) : await ensureOrtho2dOn(layer);
      if (ok) setOrthoKindChecked((prev) => ({ ...prev, [kind]: true }));
      return;
    }
    if (kind === '3d') await ensureOrtho3dOff(layer);
    else ensureOrtho2dOff(layer);

    // 二级图层全部取消后，一级主复选框同步取消
    const map = getMap?.();
    const anyLeft = (layers || []).some((ly) => (
      kind === '3d' ? isOrthoGlbOnMap(map, ly.id) : isOrthoRasterOnMap(map, ly.id)
    ));
    if (!anyLeft) {
      setOrthoKindChecked((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const confirmOrthoLoadBbox = async (mode, wgs) => {
    const kind = mode === 'ortho-3d' ? '3d' : '2d';
    const kindLabel = kind === '3d' ? '3D 建模' : '2D 航拍';
    const hits = (layers || []).filter((ly) => {
      if (kind === '3d' && !ly.glbOssKey) return false;
      const lb = parseMapLayerBounds(ly.bounds);
      if (!lb) return false;
      return bboxIntersects(wgs, lb);
    });
    const settled = await Promise.allSettled(
      hits.map((ly) => (kind === '3d' ? ensureOrtho3dOn(ly) : ensureOrtho2dOn(ly)))
    );
    const onCount = settled.filter((row) => row.status === 'fulfilled' && row.value).length;
    if (hits.length > 0) {
      setOrthoKindChecked((prev) => ({ ...prev, [kind]: true }));
      if (!isMobile) {
        setOrthoKindMenuOpen((prev) => ({ ...prev, [kind]: true }));
      }
    }
    if (hits.length < 1) {
      message.warning(`框选范围内没有可叠加的${kindLabel}`);
    } else {
      message.success(`已叠加框选区内 ${onCount}/${hits.length} 条${kindLabel}`);
    }
  };

  return {
    glbViewer,
    setGlbViewer,
    glbMapActive,
    setGlbMapActive,
    glbLoading,
    orthoKindChecked,
    setOrthoKindChecked,
    orthoKindMenuOpen,
    setOrthoKindMenuOpen,
    ortho2dActive,
    setOrtho2dActive,
    poseEditor,
    poseSaving,
    openGlbViewer,
    attachGlbToLayer,
    openPoseEditor,
    previewModelPose,
    saveModelPose,
    closePoseEditor,
    toggleGlbOnMap,
    toggleLayerOnMap,
    flyToOrthoLayer,
    flyToOrtho3d,
    ensureOrtho2dOn,
    ensureOrtho2dOff,
    ensureOrtho3dOn,
    ensureOrtho3dOff,
    turnOffOrthoKindOverlays,
    openOrthoKindMenu,
    onOrthoKindLabelOpenChange,
    onOrthoKindMasterCheck,
    onOrthoItemCheck,
    confirmOrthoLoadBbox
  };
}
